import * as React from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import { ROUTES } from 'app/constants/routes'

import postLoginContainer from './containers/login/postLoginContainer'
import { userManager } from '@/config'
import { AppLoader } from './AppLoader'

import { ProtectedAppRoutes } from './ProtectedAppRoutes'
import { ContactPage, LegalDocumentPage } from '@/modules/legal'
import {
  PostRegisterPage,
  PlanPickerPage,
  ExpiredPage,
  PaymentSuccessPage,
  MySubscriptionPage,
} from '@/modules/subscription'
import { RequireAuth } from 'app/components/application/protectedRoute'
import { MobileCheckoutPage, MobileSuccessPage } from '@/modules/mobilePurchase'
import { AppLayoutNoCompany } from '@/modules/shared/layouts/AppLayoutNoCompany'
import { UserProvider } from '@/modules/shared/context/UserContext'
import { useQueryTenantMe } from '@/modules/subscription/api/useQueryTenantMe'
import { Loader } from '@/ui'

type Props = {
  authenticated: boolean
}

function LoggedOutRedirect() {
  React.useEffect(() => {
    // Bez url_state — po ponownym zalogowaniu user ma trafić na domyślny
    // dashboard, nie z powrotem na /logged-out.
    void userManager.signinRedirect()
  }, [])
  return <AppLoader />
}

// Strony poza firmą nie są pod ProtectedApp, więc UserProvider trzeba dać tutaj.
const NoCompanyPage = ({ children }: { children: React.ReactNode }) => (
  <RequireAuth>
    <UserProvider>
      <AppLayoutNoCompany>{children}</AppLayoutNoCompany>
    </UserProvider>
  </RequireAuth>
)

function OwnTenantSubscriptionPage() {
  const { data: tenant, isLoading } = useQueryTenantMe()
  if (isLoading) return <Loader className="tw-h-screen tw-w-full" />
  return <MySubscriptionPage companyId={tenant?.companyId} />
}

// const DISPLAY_OLD_ROUTES = true // @TODO: Temporary for testing

export function AppRoutes({ authenticated }: Props) {
  const PostLoginComponent = postLoginContainer as unknown as React.ComponentType
  return (
    <Routes>
      <Route path={ROUTES.Legal.Terms} element={<LegalDocumentPage slug="regulamin" />} />
      <Route path={ROUTES.Legal.Privacy} element={<LegalDocumentPage slug="polityka-prywatnosci" />} />
      <Route path={ROUTES.Legal.Dpa} element={<LegalDocumentPage slug="dpa" />} />

      {/* Publiczne dane kontaktowe usługodawcy (wymóg weryfikacyjny Przelewy24).
          Alias /contact dla linków obcojęzycznych. */}
      <Route path={ROUTES.Legal.Contact} element={<ContactPage />} />
      <Route path="/contact" element={<Navigate to={ROUTES.Legal.Contact} replace />} />

      {/* Legacy static legal URLs were shared with external parties before the
          SPA routes existed; redirect them to the new routes. (The .pdf files
          under /legal/ are still served directly.) */}
      <Route path="/legal/polityka-prywatnosci.html" element={<Navigate to={ROUTES.Legal.Privacy} replace />} />
      <Route path="/legal/regulamin.html" element={<Navigate to={ROUTES.Legal.Terms} replace />} />
      <Route path="/legal/dpa.html" element={<Navigate to={ROUTES.Legal.Dpa} replace />} />

      <Route path={ROUTES.PostLogin} element={<PostLoginComponent />} />

      {/* Cel post_logout_redirect_uri (jedyny na whitelist klienta Keycloak).
          Sesja SSO jest już ubita, więc od razu przenosimy na formularz
          logowania — bez strony "wylogowano". */}
      <Route path={ROUTES.LoggedOut} element={<LoggedOutRedirect />} />

      {/* Standalone mobile-app purchase (EasyTachoPrivate realm). NOT wrapped in
          RequireAuth — these pages do their own auth against the private realm
          (token forwarded from the React Native app, or a fallback login form),
          decoupled from the company-realm OIDC userManager. */}
      <Route path="/mobile/checkout" element={<MobileCheckoutPage />} />
      <Route path="/mobile/success" element={<MobileSuccessPage />} />

      {/* Subscription flow: authenticated, but NOT gated by an active subscription.
          Wrapped in the minimal layout so the user keeps the sidebar (logo,
          language switcher, logout) without seeing company-scoped tabs. */}
      <Route path="/post-register" element={<NoCompanyPage><PostRegisterPage /></NoCompanyPage>} />
      <Route path="/checkout" element={<NoCompanyPage><PlanPickerPage /></NoCompanyPage>} />
      <Route path="/subscription/expired" element={<NoCompanyPage><ExpiredPage /></NoCompanyPage>} />
      <Route path="/subscription/success" element={<NoCompanyPage><PaymentSuccessPage /></NoCompanyPage>} />
      <Route path="/subscription/cancel" element={<NoCompanyPage><PlanPickerPage /></NoCompanyPage>} />

      {/* „Moja subskrypcja" z sidebara onboardingu: abonament macierzystej firmy
          z /tenant/me. Wersja firmowa to /et/company/:id/my-subscription. */}
      <Route path="/profile" element={<NoCompanyPage><OwnTenantSubscriptionPage /></NoCompanyPage>} />

      <Route path="*" element={<ProtectedAppRoutes />} />
    </Routes>
  )
}
